import EditableField from '@wandb/weave/common/components/EditableField';
import {A, TargetBlank} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import {isMobile} from '@wandb/weave/components/WeavePanelBank/panelbankFlow';
import copyToClipboard from 'copy-to-clipboard';
import React, {useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown, Icon, Message, Modal} from 'semantic-ui-react';

import DeleteRunModal from '../../components/DeleteRunModal';
import * as GQLTypes from '../../types/graphql';
import DOC_URLS from '../../util/doc_urls';
import * as RunHelpers from '../../util/runhelpers';
import * as urls from '../../util/urls';

type RunPageHeaderProps = {
  readOnly: boolean;
  project: GQLTypes.Project;
  run: GQLTypes.RunPageRunQuery;
  jobId?: string;
  cloneRun: (jobId: string) => void;
  patchRun(fields: {
    displayName?: string;
    notes?: string;
    tags?: string[];
  }): void;
  deleteRun(id: string, deleteArtifacts?: boolean): void;
};

const RunPageHeader = ({
  readOnly,
  project,
  run,
  patchRun,
  deleteRun,
  jobId,
  cloneRun,
}: RunPageHeaderProps) => {
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const handleDropdownItemClick = (callback?: () => void) => {
    setIsDropdownOpen(false);
    callback?.();
  };
  const runUrl = urls.run({
    entityName: project.entityName,
    projectName: project.name,
    name: run.name,
  });
  const createRunCodeSnippet = `import wandb
api = wandb.Api()
run = api.run("${runUrl}")`;

  const hasGitHub = run.commit && run.github;
  const codeIcon = hasGitHub ? 'github' : 'code';
  return (
    <div className="name-header-wrapper">
      <EditableField
        className="name-header"
        readOnly={readOnly}
        asHeader="h1"
        placeholder={run.name}
        showEditIcon
        value={run.displayName}
        save={value => {
          patchRun({displayName: value});
          window.analytics?.track('Run name edited');
        }}
      />
      <div className="name-header-right">
        <Button
          data-test="clone-run-button"
          icon="rocket-launch"
          variant="secondary"
          disabled={jobId == null || isMobile()}
          onClick={() => cloneRun(jobId ?? '')}
          tooltip={
            isMobile()
              ? 'Cloning runs is not supported on mobile devices.'
              : jobId != null
              ? undefined
              : 'This run cannot be cloned because it is not associated with a Launch job.'
          }>
          Clone run with Launch
        </Button>
        <Dropdown
          className="dropdown-menu-icon-button"
          floating
          trigger={
            <Button
              icon="overflow-horizontal"
              variant="secondary"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            />
          }
          direction="left"
          open={isDropdownOpen}
          // onOpen={() => setIsDropdownOpen(true)}
          // onClose={() => setIsDropdownOpen(false)}
        >
          <Dropdown.Menu>
            <Modal
              on="click"
              trigger={
                <Dropdown.Item
                  icon="database"
                  content="Export Data"
                  onClick={() => handleDropdownItemClick()}
                />
              }>
              <Modal.Header>
                <h1>Python API</h1>
              </Modal.Header>
              <Modal.Content>
                <h4>Create the run object:</h4>
                <pre className="language-python" style={{padding: '0em'}}>
                  {createRunCodeSnippet}
                </pre>
                <h4>Show history data:</h4>
                <pre className="language-python">{`print(run.history())`}</pre>
                <p style={{marginTop: 20}}>
                  Check out the{' '}
                  <TargetBlank href={DOC_URLS.publicApi}>API docs</TargetBlank>
                  {' for more information.'}
                </p>
              </Modal.Content>
            </Modal>
            <Modal
              on="click"
              trigger={
                <Dropdown.Item
                  icon={codeIcon}
                  content="View Code"
                  onClick={() => handleDropdownItemClick()}
                />
              }>
              <Modal.Header>
                <h1>View Code</h1>
              </Modal.Header>
              <Modal.Content>
                <Message icon>
                  {<Icon name={codeIcon} />}
                  <Message.Content>
                    <p>
                      Wandb will automatically save the git commit ID of the
                      last commit before each of your runs.
                    </p>
                    {hasGitHub ? (
                      <>
                        <p>
                          You can find your commit{' '}
                          <TargetBlank href={run.github}>
                            here on GitHub
                          </TargetBlank>
                          .
                        </p>
                      </>
                    ) : run.runInfo && run.runInfo.git ? (
                      <table className="run-page-table">
                        <tr>
                          <td className="key">
                            <span className="overview-key">Git remote</span>
                          </td>
                          <td className="command">
                            {run.runInfo.git.remote ?? '-'}
                          </td>
                        </tr>
                        <tr>
                          <td className="key">
                            <span className="overview-key">Git commit</span>
                          </td>
                          <td className="command">
                            {run.runInfo.git.commit ?? '-'}
                          </td>
                        </tr>
                      </table>
                    ) : (
                      <p>
                        However, we couldn't find a git commit for this
                        particular run.
                      </p>
                    )}
                    {RunHelpers.hasDiffPatchFile(run) && (
                      <p>
                        Your uncommitted changes were saved in file called{' '}
                        <strong>diff.patch</strong>, which you can download{' '}
                        <A
                          href={urls.runDiffPatchFileUrl({
                            entityName: project.entityName,
                            projectName: project.name,
                            runName: run.name,
                          })}
                          download="diff.patch">
                          here
                        </A>
                      </p>
                    )}
                  </Message.Content>
                </Message>
              </Modal.Content>
            </Modal>
            {run.internalID && (
              <Dropdown.Item
                icon="copy"
                content="Copy Immutable Run ID"
                onClick={() => {
                  copyToClipboard(run.internalID!);
                  handleDropdownItemClick();
                  window.analytics?.track('Immutable Run ID copied');
                }}
              />
            )}
            {!readOnly && (
              <>
                <Dropdown.Divider />
                {isDeleteModalOpen && (
                  <DeleteRunModal
                    closeModal={() => setDeleteModalOpen(false)}
                    isOpen={isDeleteModalOpen}
                    setIsModalOpen={setDeleteModalOpen}
                    entityName={project.entityName}
                    isRunActive={run.state === 'running'}
                    projectName={project.name}
                    runDisplayName={run.displayName}
                    runId={run.id}
                    runName={run.name}
                  />
                )}
                <Dropdown.Item
                  icon="trash"
                  content="Delete run"
                  onClick={() =>
                    handleDropdownItemClick(() => setDeleteModalOpen(true))
                  }
                />
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};

export default RunPageHeader;
